import type { PortalCartRecommendationDto } from '@supply-mover-2-0/shared';
import {
	ImagePlaceholder,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import * as cartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';
import { AddToCartControl } from './AddToCartControl';

/**
 * "Frequently bought with what's in your cart" strip (PD-13185).
 *
 * Reacts to the current cart contents in real time: the server seeds the
 * precomputed purchased-together associations with the active cart's items
 * and returns catalog-scoped, contract-priced suggestions. Renders nothing
 * when there are no associations, so carts without purchase-pattern data
 * see no extra chrome.
 */
export function CartSuggestions(): ReactElement | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const display = useDisplaySettings();
	const cart = useCartStore((s) => s.cart);

	const [suggestions, setSuggestions] = useState<
		Array<PortalCartRecommendationDto>
	>([]);

	// Reseed only when the SET of items in the cart changes — quantity
	// edits don't change what pairs well with the cart.
	const idsKey = useMemo(
		() =>
			(cart?.lineItems ?? [])
				.map((line) => line.fkItemId)
				.filter((id): id is number => id != null)
				.sort((a, b) => a - b)
				.join(','),
		[cart?.lineItems],
	);

	const requestSeq = useRef(0);

	useEffect(() => {
		if (token == null || idsKey === '') {
			setSuggestions([]);
			return;
		}

		const seq = ++requestSeq.current;
		// Debounce so rapid cart edits coalesce into one request.
		const timer = setTimeout((): void => {
			cartService
				.getCartRecommendations(token, 4)
				.then((response) => {
					if (seq !== requestSeq.current) {
						return;
					}
					setSuggestions(response.success ? response.data : []);
				})
				.catch(() => {
					if (seq === requestSeq.current) {
						setSuggestions([]);
					}
				});
		}, 600);

		return (): void => {
			clearTimeout(timer);
		};
	}, [token, idsKey]);

	if (suggestions.length === 0) {
		return null;
	}

	return (
		<div className="mb-6">
			<h2 className="text-sm font-semibold text-default mb-3">
				{t('Frequently bought together')}
			</h2>
			{/* Container-responsive (NOT viewport-responsive): this strip now lives in
			    the cart's narrower left column, so size the cards to the available
			    width — each is at least 150px (enough for the price + Add button) and
			    the column count adapts. A fixed md:grid-cols-4 overflowed the cards in
			    the ~400px left column at tablet widths. */}
			<div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
				{suggestions.map((suggestion) => {
					const displayPrice = suggestion.contractPrice ?? suggestion.price;

					return (
						<div
							key={suggestion.itemId}
							className="bg-brand-surface border border-brand-border rounded-xl p-3 flex flex-col"
						>
							<div
								className="w-full aspect-square bg-brand-input rounded-lg flex items-center justify-center overflow-hidden cursor-pointer mb-2"
								onClick={(): void => {
									void navigate(`/product/${suggestion.itemId}`);
								}}
							>
								<ResilientImage
									src={suggestion.primaryImageUrl}
									alt={t(suggestion.description ?? '')}
									className="w-full h-full object-cover"
									fallback={<ImagePlaceholder />}
								/>
							</div>
							<h3
								className="text-xs font-medium text-default line-clamp-2 cursor-pointer hover:text-portal-accent"
								onClick={(): void => {
									void navigate(`/product/${suggestion.itemId}`);
								}}
							>
								{t(suggestion.description ?? '-')}
							</h3>
							{suggestion.externalId != null && (
								<p className="text-[11px] text-brand-muted truncate">
									{t(suggestion.externalId)}
								</p>
							)}
							<div className="mt-auto pt-2 flex items-center justify-between gap-2">
								{/* PD-14239 / PD-14775: hide the suggestion price when
								    catalog pricing is off for this visitor — per-user
								    hidePricing or the site-wide hideCatalogPricing setting
								    (canSeeCatalogPricing=false). The empty span keeps the
								    Add button right-aligned via justify-between, matching
								    the existing null-price layout. */}
								{display.canSeeCatalogPricing && displayPrice != null ? (
									<span className="text-sm font-semibold text-default">
										{formatCurrency(displayPrice)}
									</span>
								) : (
									<span />
								)}
								<AddToCartControl
									itemId={suggestion.itemId}
									variant="compact"
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
